import React, {useState} from 'react'
import {Box, Button, Link, Select, SimpleGrid, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import InformativeLabel from '../../components/informative-label'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useHistory} from 'react-router'
import {getInfoLabelVariantByOmsStatus} from '../../pages/account/my-orders/utils'
import PropTypes from 'prop-types'

const SubscriptionsBox = ({subscription, detailPage = false}) => {
    const styles = useMultiStyleConfig('Subscriptions')
    const {locale, buildUrl} = useMultiSite()
    const localeId = locale.id.toLowerCase()
    const history = useHistory()
    const [selectedOrder, setSelectedOrder] = useState('')
    const {formatDate} = useIntl()

    // Navigate to subscription details page
    const handleSubscriptionClick = (subscriptionId) => {
        history.push(`/${localeId}/account/subscription-details/${subscriptionId}`)
    }

    const handleSelectChange = (e) => {
        const selectedId = e.target.value
        const selected = subscription?.orderSummaries?.find((opt) => opt.id === selectedId)
        setSelectedOrder(selected)
    }
    const parseSubscriptionContent = (htmlString) => {
        if (!htmlString) return []
        const content = htmlString
            .replace(/<\/?p>/g, '')
            .split(/<br\s*\/?>/g)
            .filter((item) => item.trim())
        return content
    }

    return (
        <Box {...styles.subscriptionGap}>
            {!detailPage && (
                <Text {...styles.subscriptionNumberHeader}>
                    <FormattedMessage
                        id="account_subscription_history.label.subscription_number"
                        defaultMessage="Subscription number: {subscriptionNumber}"
                        values={{
                            subscriptionNumber: subscription?.name,
                        }}
                    />
                </Text>
            )}
            <Box {...styles.subscriptionBoxContainer}>
                <SimpleGrid {...styles.subscriptionBoxUpContainer}>
                    <Box {...styles.subscriptionInnerBoxContainer}>
                        <Text {...styles.subscriptionBoxLabel}>
                            <FormattedMessage
                                id="account_subscription_history.data.content"
                                defaultMessage="Content"
                            />
                        </Text>
                        <Box as="ul" listStylePosition="inside">
                            {parseSubscriptionContent(subscription?.subscriptionCommercialName).map(
                                (item, index) => (
                                    <Box as="li" key={index}>
                                        {item.trim()}
                                    </Box>
                                ),
                            )}
                        </Box>
                    </Box>

                    <Box {...styles.subscriptionInnerBoxContainer}>
                        <Text {...styles.subscriptionBoxLabel}>
                            <FormattedMessage
                                id="account_subscription_history.data.subscription_date"
                                defaultMessage="Subscription date"
                            />
                        </Text>
                        <Text>{formatDate(subscription?.subscriptionStartDate)}</Text>
                    </Box>
                    <Box {...styles.subscriptionInnerBoxContainer}>
                        <Text {...styles.subscriptionBoxLabel}>
                            <FormattedMessage
                                id="account_subscription_history.data.subscription_duration"
                                defaultMessage="Subscription duration"
                            />
                        </Text>
                        <Text>
                            <FormattedMessage
                                id="account_subscription_history.data.subscription_duration.value"
                                defaultMessage="{duration} months"
                                values={{
                                    duration:
                                        subscription?.renewalPeriodLength ||
                                        subscription?.renewal?.periodLength,
                                }}
                            />
                        </Text>
                    </Box>
                    <Box {...styles.subscriptionStatusDetailsContainer}>
                        <Box {...styles.subscriptionStatusContainer}>
                            <Text {...styles.subscriptionBoxLabel}>
                                <FormattedMessage
                                    id="account_subscription_history.data.status"
                                    defaultMessage="Status"
                                />
                            </Text>
                            <InformativeLabel
                                label={subscription?.status}
                                width="fit-content"
                                variant={getInfoLabelVariantByOmsStatus(
                                    subscription?.status?.toLowerCase(),
                                )}
                            />
                        </Box>
                        {!detailPage && (
                            <Box {...styles.subscriptionDetailsLabelContainer}>
                                <Text
                                    {...styles.subscriptionDetailsLabel}
                                    as="a"
                                    onClick={() => handleSubscriptionClick(subscription?.id)}
                                >
                                    <FormattedMessage
                                        id="account_subscription_history.data.details"
                                        defaultMessage="Details"
                                    />
                                </Text>
                            </Box>
                        )}
                    </Box>
                </SimpleGrid>
                {detailPage && (
                    <Box {...styles.subscriptionBoxBottomContainer}>
                        <Text {...styles.subscriptionBoxLabel}>
                            <FormattedMessage
                                id="account_subscription_history.data.shipped_orders"
                                defaultMessage="Shipped orders total: {ordersTotal}"
                                values={{
                                    ordersTotal: subscription?.orderSummaries?.length,
                                }}
                            />
                        </Text>
                        {subscription?.status === 'Active' && (
                            <Text>
                                <FormattedMessage
                                    id="account_subscription_history.data.subscription_duration"
                                    defaultMessage="Next order execution date: {date}"
                                    values={{
                                        date: formatDate(subscription?.renewal?.nextDate),
                                    }}
                                />
                            </Text>
                        )}

                        <Box {...styles.selectOrderContainer}>
                            <Select
                                value={selectedOrder.id}
                                onChange={handleSelectChange}
                                placeholder={
                                    <FormattedMessage
                                        id="account_subscription_history.data.select_order"
                                        defaultMessage="Select order"
                                    />
                                }
                                {...styles.selectOrderSelect}
                            >
                                {subscription?.orderSummaries?.map((opt) => (
                                    <option key={opt.id} value={opt.id}>
                                        {opt.orderNumber}
                                    </option>
                                ))}
                            </Select>
                            <Button
                                as={Link}
                                href={
                                    selectedOrder
                                        ? buildUrl(
                                              `/account/orders/${selectedOrder.id}/${selectedOrder.orderNumber}/${selectedOrder.originalOrderId}`,
                                          )
                                        : '#'
                                }
                                variant="primary"
                                isDisabled={!selectedOrder}
                                {...styles.selectOrderButton}
                            >
                                <FormattedMessage
                                    id="account_subscription_history.data.button"
                                    defaultMessage="Go to order detail"
                                />
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

SubscriptionsBox.propTypes = {
    subscription: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        status: PropTypes.string,
        subscriptionStartDate: PropTypes.string,
        subscriptionCommercialName: PropTypes.string,
        renewalPeriodLength: PropTypes.number,
        subscriptionItems: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
            }),
        ),
        orderSummaries: PropTypes.array,
        renewal: PropTypes.object,
    }),
    detailPage: PropTypes.bool,
}

export default SubscriptionsBox
